import React, { useState, useEffect } from "react";
import { Row, Col, Form, Input, Select, Button, InputNumber, Typography, Divider, Segmented, Switch, Tag } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import ModelWrapper from "../../../modal/ModelWrapper";
import { CustomButton, CustomSecondaryButton } from "../../physician_page/styled";
import { notify } from "../../../../utils/helper";
import styled from "styled-components";
import { extractToken } from "../../../../utils/localStorage";
import { globalRoles } from "../../../../utils/roles";
import videoService from "../../../../services/videos";
import { ccmCoordinatorService, physicianService } from "../../../../services/user";
import journeyService from "../../../../services/journey";
import { capitalizeFirstLetter, formatDuration } from "../../../../utils/utils";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const ModalContent = styled.div`
  padding: 0;
  max-height: 70vh;
  overflow-y: auto;
  // background: var(--border-color);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-text-color);
  margin-bottom: 12px;
  margin-top: 0;
`;

const Panel = styled.div`
  background: var(--primary-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.04);
  padding: 16px 16px;
  margin: 0 10px 12px 10px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const PanelTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--primary-text-color);
`;

const PanelBody = styled.div`
  margin-top: 8px;
`;

const VideoStepContainer = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 14px 14px 14px 56px;
  margin-bottom: 12px;
  background-color: var(--border-color);
  position: relative;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.04);
`;

const StepNumber = styled.div`
  position: absolute;
  top: 14px;
  left: 16px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #8a3ef4;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(138, 62, 244, 0.4);
`;

const StepContent = styled.div`
  margin-left: 0;
`;

const StepHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const StepTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--primary-text-color);
`;

export const StyledSelect = styled(Select)`
  width: 100%;
  border-radius: 12px;
  .ant-select-selector {
    height: 44px !important;
    border-radius: 10px !important;
    background-color: var(--primary-bg) !important;
    border: 1px solid var(--border-color) !important;
  }

  .ant-select-selection-item {
    color: var(--primary-text-color);
    display: flex;

  }

  &:hover .ant-select-selector {
    border-color: #8a3ef4 !important;
    box-shadow: 0 0 0 3px rgba(138, 62, 244, 0.12);
  }
`;

const AddStepButton = styled(Button)`
  width: 100%;
  height: 46px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background-color: var(--primary-bg);
  color: var(--primary-text-color) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;

  &:hover {
    border-color: var(--border-color) !important;
    color: var(--primary-text-color) !important;
    background-color: var(--secondary-bg) !important;
  }
`;

const DeleteButton = styled(Button)`
  color: #ff4d4f;
  border: none;
  background: transparent;
  padding: 0;
  height: 30px;

  &:hover {
    background-color: transparent;
    color: #ff7875;
  }
  
  svg {
    margin: 0px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin: 8px 16px 12px 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
`;

const ToggleContainer = styled.div`
  margin: 0px 10px 12px;
  padding: 16px 16px;
  background: var(--primary-bg);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-color);

  .ant-switch-inner {
    display: block;
    margin: 0px 28px 0px 25px;
    color: var(--primary-text-color);
    font-size: 12px;
    transition: margin .2s; 
    font-weight: 700;
    font-family: "Inter", sans-serif;
  }

  .toggle-label {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .ant-typography {
      margin: 0;
      font-weight: 500;
      color: var(--primary-text-color);
    }
  }
  .ant-switch-checked {
    background-color: rgba(138, 62, 244, 0.7) !important;
  }
  .ant-switch:focus{
    box-shadow: none;
  }
`;

const CreateJourneyModal = ({ isOpen, setOpen, isEdit,setIsEdit, journeyId, onSuccess, templateData, isCreateJourneyByTemplate }) => {
  const [form] = Form.useForm();
  const [videos, setVideos] = useState([]);
  const [ccmManagers, setCcmManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [videoSteps, setVideoSteps] = useState([{ step: 1, videoId: null }]);
  const [fetchingJourney, setFetchingJourney] = useState(false);
  const [isType, setIsType] = useState("clinical");
  const [allPhysicians, setAllPhysicians] = useState([]);
  const user = extractToken();
  const isSuperAdmin = user?.role === globalRoles.SUPERADMIN;
  const isCoordinator = user?.role === globalRoles.CCM_COORDINATOR;
  const isManager = user?.role === globalRoles.CCM_MANAGER;
  const isLibraryManager = user?.role === globalRoles.LIBRARY_MANAGER;


  const fetchVideos = async (journeyType = null, journeySteps = []) => {
    try {
      setLoading(true);
      const response = await videoService.getVideos(100, 1, "", "", "", "", "", "");
      if (response?.data?.videos?.results) {
        let filteredVideos = response.data.videos.results;
        
        // Filter by journey type if provided
        if (journeyType === "ccm") {
          filteredVideos = filteredVideos.filter(
            (video) => video.videoType === "ccm"
          );
        } else if (journeyType === "clinical") {
          filteredVideos = filteredVideos.filter(
            (video) => video.videoType !== "ccm" || !video.videoType
          );
        }
        
        // When editing, ensure videos from journey steps are included
        if (journeySteps && journeySteps.length > 0) {
          const stepVideoIds = journeySteps
            .map((step) => step.video?.id || step.videoId)
            .filter(Boolean);
          
          const stepVideos = response.data.videos.results.filter((video) =>
            stepVideoIds.includes(video.id)
          );
          
          // Merge step videos with filtered videos, avoiding duplicates
          const allVideoIds = new Set(filteredVideos.map((v) => v.id));
          stepVideos.forEach((video) => {
            if (!allVideoIds.has(video.id)) {
              filteredVideos.push(video);
              allVideoIds.add(video.id);
            }
          });
        }
        
        setVideos(filteredVideos);
      } else {
        setVideos([]);
      }
    } catch (error) {
      notify("error", "Failed to load videos");
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCCMManagers = async () => {
    try {
      const response = await ccmCoordinatorService.getAll(100, 1, "", "");
      if (response?.data?.results) {
        setCcmManagers(
          response.data.results.map((item) => ({
            id: item.id || item._id,
            name: item.name,
            email: item.email,
          }))
        );
      } else {
        setCcmManagers([]);
      }
    } catch (error) {
      notify("error", "Failed to load CCM managers");
      setCcmManagers([]);
    }
  };

  const fetchAllPhysician = async () => {
    try {
      const response = await physicianService.getAll(0, 1, "", "", "");
      if (response?.data?.results) {
        setAllPhysicians(
          response.data.results.map((item) => ({
            id: item.id || item._id,
            name: item.name,
            email: item.email,
          }))
        );
      } else {
        setAllPhysicians([]);
      }
    } catch (error) {
      notify("error", "Failed to load physician");
      setAllPhysicians([]);
    }
  };


  useEffect(() => {
    if (isOpen) {
      if (isSuperAdmin || isLibraryManager) {
        fetchCCMManagers();
        fetchAllPhysician();
      }
      
      if (templateData) {
        // Pre-fill form with template data
        const type = templateData.journeyType || templateData.templateType || "clinical";
        setIsType(type);

        form.setFieldsValue({
          journeyType: type,
          title: templateData.name || "",
          description: templateData.description || "",
        });
        
        // Set video steps from template
        if (templateData.steps && templateData.steps.length > 0) {
          const steps = templateData.steps.map((step, index) => ({
            step: index + 1,
            videoId: step.video?.id || step.videoId || null,
            title: step.video?.title || step.title || "",
          }));
          setVideoSteps(steps);

          const formValues = {};
          steps.forEach((s, index) => {
            formValues[`videoStep_${index}`] = s.videoId;
          });
          form.setFieldsValue(formValues);
          
          // Fetch videos and ensure template videos are included
          // Pass original template steps so fetchVideos can access step.video.id
          fetchVideos(type, templateData.steps);
        } else {
          setVideoSteps([{ step: 1, videoId: null }]);
          fetchVideos(type);
        }
      } else if (isEdit && journeyId) {
        fetchVideos(journeyId?.type);
      } else {
        form.resetFields();
        form.setFieldsValue({ journeyType: "clinical" });
        setVideoSteps([{ step: 1, videoId: null }]);
        fetchVideos(isType);
      }
    }
  }, [isOpen, isSuperAdmin, isEdit, journeyId, templateData, form]);

  useEffect(() => {
    if (isOpen && !isEdit) {
      form.resetFields();
      fetchVideos(isType);
    }
  }, [isType, isOpen]);

  const handleAddStep = () => {
    const nextStep = videoSteps.length + 1;
    setVideoSteps([...videoSteps, { step: nextStep, videoId: null }]);
  };

  const handleRemoveStep = (index) => {
    if (videoSteps.length > 1) {
      const newSteps = videoSteps.filter((_, i) => i !== index);
      // Re-number steps
      const renumberedSteps = newSteps.map((step, i) => ({
        ...step,
        step: i + 1,
      }));
      setVideoSteps(renumberedSteps);

      const values = {};
      renumberedSteps.forEach((s, i) => {
        values[`videoStep_${i}`] = s.videoId;
      });

      form.setFieldsValue(values);

      form.resetFields([`videoStep_${renumberedSteps.length}`]);
    }
  };

  const handleVideoChange = (stepIndex, videoId) => {
    const newSteps = [...videoSteps];
    newSteps[stepIndex].videoId = videoId;
    setVideoSteps(newSteps);
    form.setFieldsValue({
      videoSteps: newSteps.map((s) => s.videoId),
    });
  };

  const handleSubmit = async (values) => {
    if (videoSteps.some((step) => !step.videoId)) {
      notify("warning", "Please select a video for all steps");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        type: isType,
        name: values.title,
        description: values.description || "",
        ...((isSuperAdmin || isLibraryManager) && (
          isType === "clinical" 
          ? {physicianId: values.ccmManager || ""} 
          : {coordinatorId: values.ccmManager || ""}
        )),
        status: values.status || "",
        steps: videoSteps.map((step, index) => ({
          videoId: step.videoId,
          title: "",
          order: index,
        })),
      };

      if (isEdit && journeyId) {
        await journeyService.updateJourney(journeyId.id, payload);
        notify("success", "Journey updated successfully");
      } else {
      await journeyService.createJourney(payload);
      notify("success", "Journey created successfully");
      }

      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (error) {
      notify("error", isEdit ? "Failed to update journey" : "Failed to create journey");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setVideoSteps([{ step: 1, videoId: null }]);
    setOpen(false);
    if (setIsEdit) {
      setIsEdit(false);
    }
    // if (onSuccess) {
    //   onSuccess();
    // }
  };

  // Get selected video IDs to filter them out from other steps
  const getSelectedVideoIds = (currentIndex) => {
    return videoSteps
      .map((step, index) => (index !== currentIndex ? step.videoId : null))
      .filter((id) => id !== null);
  };

  useEffect(() => {
    if (!templateData) {
    const canPopulate =
    isEdit &&
    journeyId &&
    (!isSuperAdmin || (isSuperAdmin && ccmManagers.length > 0));

    if (canPopulate) {
      form.setFieldsValue({
        journeyType: journeyId.type || "",
        title: journeyId.name || "",
        description: journeyId.description || "",
        ccmManager: journeyId?.coordinatorId?.id || journeyId?.physicianId?.id || undefined,
        billingCycle: journeyId?.billingCycle || undefined,
        status: journeyId?.status || undefined,
        minutes: journeyId?.minutes ?? 0,
      });
      setIsType(journeyId?.type);
    } else {
      form.setFieldsValue({
        journeyType: undefined,
        title: "",
        description: "",
        ccmManager: undefined,
        billingCycle: undefined,
        status: undefined,
        minutes: 0,
      });
      if(isManager || isCoordinator) {
        setIsType("ccm")
      } else {
        setIsType("clinical");
      }
    }
  }
  }, [journeyId, ccmManagers, form, isEdit]);

  useEffect(() => {
    if (journeyId?.steps?.length) {
      const videoStepsData = journeyId.steps.map((s) => ({
        step: s.order + 1,
        videoId: s.video.id,
      }));

      setVideoSteps(videoStepsData);

      const formValues = {};
      journeyId.steps.forEach((s, index) => {
        formValues[`videoStep_${index}`] = s.video.id;
      });

      form.setFieldsValue(formValues);
    }
  }, [journeyId, form]);
  

  return (
    <ModelWrapper
      show={isOpen}
      setShow={setOpen}
      title={
        <div className="row-flex flex-column">
          <div className="font-18 font-semibold text-primary">
            {isEdit ? "Edit Journey" : templateData ? "Create Journey from Template" : "Create New Journey"}
          </div>
          <div className="font-15 font-medium text-secondry">
            Define details and build a video sequence for this journey.
          </div>
        </div>
      }
      footer={null}
      width={750}
      keyClose={false}
      onCancel={handleClose}
      className="custom-detail-modal"
    >
      <ModalContent>
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleSubmit}
          onValuesChange={(changedValues) => {
            if (changedValues.journeyType) {
              fetchVideos(changedValues.journeyType);
            }
          }}
        >
          {(!isEdit && (isSuperAdmin || isLibraryManager)) && (
            <ToggleContainer>
              <div className="toggle-label">
                <Typography.Text>Clinical</Typography.Text>
                  <Switch
                    checked={isType === "ccm"}
                    onChange={(checked) => setIsType(checked ? 'ccm' : 'clinical')}
                    checkedChildren="CCM"
                    unCheckedChildren="Clinical"
                  />
                <Typography.Text>CCM</Typography.Text>
              </div>
            </ToggleContainer>
          )}
          <Panel>
            <PanelHeader>
              <PanelTitle>Journey Details</PanelTitle>
            </PanelHeader>
            <PanelBody>
              <Row gutter={[16, 8]}>
                {/* <Col span={24}>
                  <Form.Item
                    name="journeyType"
                    label="Journey Type"
                    rules={[{ required: true, message: "Please select journey type!" }]}
                  >
                    <StyledSegmented
                      options={[
                        { label: "Clinical", value: "clinical" },
                        { label: "CCM", value: "ccm" },
                      ]}
                      onChange={(value) => setIsType(value)}
                    />
                  </Form.Item>
                </Col> */}
                <Col span={24}>
                  <Form.Item
                    name="title"
                    label="Title"
                    rules={[{ required: true, message: "Please enter journey title!" }]}
                  >
                    <Input
                      placeholder="Enter journey title"
                      size="large"
                      style={{
                        height: 44,
                        borderRadius: 10,
                        backgroundColor: "var(--primary-bg)",
                        border: "1px solid var(--border-color)",
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="description"
                    label="Description"
                    rules={[{ required: true, message: "Please enter journey description!" }]}
                  >
                    <TextArea
                      placeholder="Enter journey description"
                      rows={4}
                      style={{
                        borderRadius: 10,
                        backgroundColor: "var(--primary-bg)",
                        border: "1px solid var(--border-color)",
                      }}
                    />
                  </Form.Item>
                </Col>
                {/* {isType === "ccm" && (
                  <>
                    <Col span={12}>
                      <Form.Item
                        name="billingCycle"
                        label="Billing Cycle"
                        rules={[{ required: true, message: "Please select billing cycle!" }]}
                      >
                        <StyledSelect
                          getPopupContainer={(triggerNode) => triggerNode.parentNode}
                          placeholder="Select billing cycle"
                          size="large"
                        >
                          <Option value="monthly">Monthly</Option>
                          <Option value="yearly">Yearly</Option>
                        </StyledSelect>
                      </Form.Item>
                    </Col>
                  </>
                )} */}
                <Col span={isType === "ccm" ? 24: (isSuperAdmin || isLibraryManager) ? 6 : 24}>
                  <Form.Item
                    name="status"
                    label="Status"
                    rules={[{ required: true, message: "Please select status!" }]}
                  >
                    <StyledSelect
                      getPopupContainer={(triggerNode) => triggerNode.parentNode}
                      placeholder="Select status"
                      size="large"
                    >
                      <Option value="draft">Draft</Option>
                      <Option value="active">Active</Option>
                      <Option value="completed">Completed</Option>
                    </StyledSelect>
                  </Form.Item>
                </Col>
                {(isSuperAdmin || isLibraryManager) && (
                  <Col span={isType === "clinical" ? 18 : 24}>
                    <Form.Item
                      name="ccmManager"
                      label={`${(isType === "clinical") ? "Physician" : "CCM Coordinator"}`}
                      rules={[{ required: true, message: `Please select a ${(isType === "clinical") ? "physician!" : "CCM coordinator!"}` }]}
                    >
                      <StyledSelect
                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                        placeholder={`Select ${(isType === "clinical") ? "Physician" : "CCM Coordinator"}`}
                        size="large"
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        {(isType === "clinical" ? allPhysicians : ccmManagers)?.map((manager) => (
                          <Option
                            key={manager.id}
                            value={manager.id}
                            label={`${manager.name} - ${manager.email}`}
                          >
                            {manager.name} - {manager.email}
                          </Option>
                        ))}
                      </StyledSelect>
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </PanelBody>
          </Panel>

          <Panel>
            <PanelHeader>
              <PanelTitle>Video Steps</PanelTitle>
            </PanelHeader>
            <PanelBody>
              <Row gutter={[0, 12]}>
                {videoSteps.map((step, index) => {
                  const selectedVideoIds = getSelectedVideoIds(index);
                  let availableVideos = videos.filter((video) => !selectedVideoIds.includes(video.id));
                  if (step.videoId) {
                    const currentVideo = videos.find((v) => v.id === step.videoId);
                    if (!currentVideo) {
                      const placeholderVideo = {
                        id: step.videoId,
                        title: step.title || "Video (Not Found)",
                      };
                      availableVideos = [placeholderVideo, ...availableVideos];
                    } else if (selectedVideoIds.includes(step.videoId)) {
                      availableVideos = [currentVideo, ...availableVideos];
                    }
                  }

                  return (
                    <Col span={24} key={index}>
                      <VideoStepContainer>
                        <StepNumber>{step.step}</StepNumber>
                        <StepContent>
                          <StepHeader>
                            <StepTitle>Step {step.step}</StepTitle>
                            {videoSteps.length > 1 && (
                              <DeleteButton
                                icon={<DeleteOutlined />}
                                onClick={() => handleRemoveStep(index)}
                                type="text"
                              />
                            )}
                          </StepHeader>
                          <Form.Item
                            name={`videoStep_${index}`}
                            rules={[{ required: true, message: "Please select a video for this step!" }]}
                          >
                            <StyledSelect
                              getPopupContainer={(triggerNode) => triggerNode.parentNode}
                              placeholder="Select a video"
                              size="large"
                              value={step.videoId}
                              onChange={(value) => handleVideoChange(index, value)}
                              showSearch
                              filterOption={(input, option) =>
                                String(option?.searchtitle ?? "").toLowerCase().includes(input.toLowerCase())
                              }
                              loading={loading}
                              optionLabelProp="label"
                            >
                              {availableVideos.map((video) => (
                                <Option 
                                  key={video.id} 
                                  value={video.id}
                                  searchtitle={video.title || ""}
                                  label={(
                                    <div className="d-flex justify-between w-100">
                                      <div className="d-flex gap-12">
                                        <VideoCameraOutlined />
                                        <span className="title">{video.title}</span>
                                      </div>
                                      <span className="duration">{formatDuration(video.duration)} ⏱</span>
                                    </div>
                                  )}
                                  style={{borderBottom: "1px solid var(--border-color)"}}
                                >
                                  {/* <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <PlayCircleOutlined />
                                    <span>{video.title || "Untitled Video"}</span>
                                  </div> */}
                                  <div
                                    className="d-flex align-items-center"
                                    style={{ gap: "12px", padding: "4px 0" }}
                                  >
                                    <div
                                      style={{
                                        backgroundColor: "var(--secondary-bg)",
                                        borderRadius: "6px",
                                        padding: "6px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        minWidth: "32px",
                                        height: "32px",
                                      }}
                                    >
                                      <VideoCameraOutlined
                                        style={{
                                          fontSize: "14px",
                                          color: "#8A3EF4",
                                        }}
                                      />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                      <div
                                        className="font-15 font-medium text-primary ellipsis"
                                        style={{ lineHeight: "1.2", width: "50%" }}
                                      >
                                        {capitalizeFirstLetter(video.title)}
                                      </div>
                                      <div
                                        className="text-secondry"
                                        style={{
                                          lineHeight: "1.2",
                                          marginTop: "2px",
                                          maxWidth: "300px",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {video.specialty && (
                                          <Tag>{capitalizeFirstLetter(video.specialty)}</Tag>
                                        )}
                                        <Tag
                                          color={
                                            !video?.generalLibrary
                                              ? video.referenceMasterVideoId
                                                ? "orange"
                                                : "purple"
                                              : "green"
                                          }
                                        >
                                          {video?.videoType ? 
                                            capitalizeFirstLetter(video?.videoType)
                                          : 
                                            !video?.generalLibrary
                                              ? video.referenceMasterVideoId
                                                ? "AI Enhanced"
                                                : "Master"
                                              : "Patient Education"
                                          }
                                        </Tag>
                                      </div>
                                    </div>
                                    {video.duration && (
                                      <div
                                        className="d-flex text-secondry"
                                        style={{ fontSize: "13px", gap: "3px" }}
                                      >
                                        {formatDuration(video.duration)}
                                        <ClockCircleOutlined />
                                      </div>
                                    )}
                                  </div>
                                </Option>
                              ))}
                            </StyledSelect>
                          </Form.Item>
                        </StepContent>
                      </VideoStepContainer>
                    </Col>
                  );
                })}
              </Row>
              <AddStepButton type="dashed" icon={<PlusOutlined />} onClick={handleAddStep}>
                Add Next Step
              </AddStepButton>
            </PanelBody>
          </Panel>

          <ButtonContainer>
            <CustomSecondaryButton onClick={handleClose} disabled={submitting}>
              Cancel
            </CustomSecondaryButton>
            <CustomButton htmlType="submit" loading={submitting || fetchingJourney} disabled={fetchingJourney}>
              {submitting ? (isEdit && isCreateJourneyByTemplate ? "Creating..." : isEdit ? "Updating..." : "Creating...") : (isEdit && isCreateJourneyByTemplate ? "Create Journey From Template" : (isEdit ? "Update Journey" : "Create Journey"))}
            </CustomButton>
          </ButtonContainer>
        </Form>
      </ModalContent>
    </ModelWrapper>
  );
};

export default CreateJourneyModal;

